import {
  toAbsoluteLogoUrl,
  toAbsolutePhotoUrl,
} from "../../../services/commercesServices";

export const FALLBACK_COMMERCE_IMAGE = "/img/no-image.jpg";

export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", maxLength = 220) {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}…`;
}

export function normalizeSearchText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function toBoolean(value) {
  return value === true || value === 1 || value === "1";
}

export function normalizeExternalUrl(value = "") {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

export function normalizePhoneHref(value = "") {
  const phone = String(value || "")
    .replace(/\s+/g, "")
    .replace(/[().-]/g, "");

  return phone ? `tel:${phone}` : "";
}

export function parseNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function buildAddress(item) {
  return [item?.adresse, item?.code_postal, item?.ville]
    .filter(Boolean)
    .map((value) => String(value).trim())
    .join(" ");
}

export function buildMapsHref({ lat, lng, address }) {
  const latitude = parseNumber(lat);
  const longitude = parseNumber(lng);

  if (latitude !== null && longitude !== null) {
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
  }

  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address
    )}`;
  }

  return "";
}

export function buildMapEmbedUrl({ lat, lng, address }) {
  const latitude = parseNumber(lat);
  const longitude = parseNumber(lng);

  const query =
    latitude !== null && longitude !== null
      ? `${latitude},${longitude}`
      : address;

  if (!query) return "";

  return `https://maps.google.com/maps?q=${encodeURIComponent(
    query
  )}&z=16&output=embed`;
}

export function formatDistance(distanceKm) {
  const num = Number(distanceKm);
  if (!Number.isFinite(num)) return "";

  if (num < 1) {
    return `${Math.round(num * 1000)} m`;
  }

  return `${num.toFixed(2)} km`;
}

export function getDistanceKm(fromLat, fromLng, toLat, toLng) {
  const lat1 = parseNumber(fromLat);
  const lng1 = parseNumber(fromLng);
  const lat2 = parseNumber(toLat);
  const lng2 = parseNumber(toLng);

  if ([lat1, lng1, lat2, lng2].some((value) => value === null)) {
    return null;
  }

  const radius = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  return radius * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

export function parseHoraires(value) {
  if (!value) return null;

  try {
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return null;
  }
}

export const COMMERCE_DAYS = [
  ["lun", "Lundi"],
  ["mar", "Mardi"],
  ["mer", "Mercredi"],
  ["jeu", "Jeudi"],
  ["ven", "Vendredi"],
  ["sam", "Samedi"],
  ["dim", "Dimanche"],
];

export function normalizeCommerce(item, options = {}) {
  const userLat = options.userLat;
  const userLng = options.userLng;

  const photo = item?.photo_url ? toAbsolutePhotoUrl(item.photo_url) : "";
  const logo = item?.logo_url ? toAbsoluteLogoUrl(item.logo_url) : "";
  const image = photo || logo || FALLBACK_COMMERCE_IMAGE;

  const title = item?.nom_commerce || "Commerce";
  const category = item?.categorie || "";
  const descriptionText = stripHtml(item?.description_commerce || "");
  const descriptionPreview = truncateText(descriptionText, 240);

  const address = buildAddress(item);
  const lat = item?.gps_lat;
  const lng = item?.gps_lng;

  const mapsHref = buildMapsHref({ lat, lng, address });
  const mapEmbedUrl = buildMapEmbedUrl({ lat, lng, address });

  const websiteUrl = normalizeExternalUrl(item?.website);
  const facebookUrl = normalizeExternalUrl(item?.facebook);
  const instagramUrl = normalizeExternalUrl(item?.instagram);

  const distanceKm =
    item?.distance_km ??
    item?.dataValues?.distance_km ??
    getDistanceKm(userLat, userLng, lat, lng);

  const normalized = {
    id: item?.id,
    raw: item,

    title,
    slug: item?.slug || item?.id,
    category,
    tags: item?.tags || "",

    descriptionRaw: item?.description_commerce || "",
    descriptionText,
    descriptionPreview,

    image,
    photo,
    logo,

    phone: item?.tel || "",
    phoneHref: normalizePhoneHref(item?.tel),
    mail: item?.mail || "",

    websiteUrl,
    facebookUrl,
    instagramUrl,

    address,
    ville: item?.ville || "",
    codePostal: item?.code_postal || "",

    lat,
    lng,
    hasMap: Boolean(address || (lat && lng)),
    mapsHref,
    mapEmbedUrl,

    moyensPaiement: item?.moyens_paiement || "",
    accessibilite: item?.accessibilite || "",
    services: item?.services || "",
    horaires: parseHoraires(item?.horaires_json),

    distanceKm,
    distanceLabel: formatDistance(distanceKm),
  };

  normalized.searchIndex = normalizeSearchText(
    [
      normalized.title,
      normalized.category,
      normalized.tags,
      normalized.descriptionText,
      normalized.address,
      normalized.ville,
      normalized.codePostal,
      normalized.phone,
      normalized.mail,
      normalized.websiteUrl,
      normalized.services,
      normalized.moyensPaiement,
      normalized.accessibilite,
    ]
      .filter(Boolean)
      .join(" ")
  );

  return normalized;
}